import { useCallback, useMemo } from 'react';

import { MutateOptions } from '@tanstack/react-query';
import omit from 'lodash-es/omit';
import { useDispatch, useSelector } from 'react-redux';

import {
  useConversationDetail,
  useConversationMutations,
} from '@/modules/humanChat/hooks/useConversations';
import { popModal } from '@/redux/modals/actions';
import { selectSessionId } from '@/redux/session/selectors';
import { Conversation, ConversationUpdate, ContextUserUpdate } from '@/shared/types/chat';

import { useDeskId } from './useDeskId';

type UpdateConversationOptions = MutateOptions<Conversation, Error, ConversationUpdate, unknown>;

export const useConversationContext = () => {
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const { getStatus, context } = useConversationDetail({ deskId, sessionId });
  const { updateConversation } = useConversationMutations({ deskId, sessionId });

  const tags = useMemo(() => context?.tags || [], [context?.tags]);
  const user = useMemo(() => context?.user, [context?.user]);
  const contextVars = useMemo(() => {
    if (!context) {
      return {};
    }
    return omit(context, 'user', 'tags');
  }, [context]);
  const filteredContextVars = useMemo(() => {
    return omit(contextVars, ['global.bundle']);
  }, [contextVars]);

  const addTag = useCallback(
    (tag: string, options?: UpdateConversationOptions) => {
      updateConversation({ context: { tags: [...(context?.tags || []), tag] } }, options);
    },
    [updateConversation, context?.tags]
  );

  const removeTag = useCallback(
    (tag: string, options?: UpdateConversationOptions) => {
      updateConversation(
        { context: { tags: (context?.tags || []).filter((t) => t !== tag) } },
        options
      );
    },
    [updateConversation, context?.tags]
  );

  const updateUser = useCallback(
    (user: ContextUserUpdate, options?: UpdateConversationOptions) => {
      updateConversation(
        { context: { user } },
        {
          onSuccess: () => {
            dispatch(popModal());
          },
          ...options,
        }
      );
    },
    [dispatch, updateConversation]
  );

  return {
    contextVars,
    filteredContextVars,
    isLoading: getStatus === 'pending',
    user,
    updateUser,
    addTag,
    removeTag,
    tags,
  };
};
